/* eslint-disable max-lines */
import { comparer } from 'mobx';

import {
  AnalyticsStore,
  codeSplit,
  ContextStore,
  Controller,
  ExperimentStore,
  FetchOptions,
  FetchPageOptions,
  merge,
  PageStore,
  SerializedData,
} from 'bernie-core';
import { formatText } from 'bernie-l10n';
import { LocationReactorResult } from 'bernie-client';
import { PageData } from 'bernie-http';

import { evaluateAndLogExperiment } from 'stores/utils';
import { getPackageType, getSearchTimeout, shouldCallMultiItemSessionMutation } from 'stores/multi-item-store-util';
import { getSearchImpactingKeys, PropertySort, SearchCriteriaModel, singleUseQueryParams, untrackedQueryParams } from 'stores/models';
import { MultiItemStore } from 'stores/multi-item-store';
import { SearchCriteriaStore } from 'stores/search-criteria-store';
import { SearchCriteriaUtils } from 'stores/models/search-criteria-utils';
import { SearchQueryAdapter } from 'stores/adapters/search-query-adapter';
import { SearchResultsStore } from 'stores/search-results-store';

import { PackageType } from 'models/multi-item/multi-item-types';
import { SearchPageName } from 'constants/pages';

import { getTrackingInfoForLogging } from 'utils/logging-utils';
import { meta } from 'utils/meta';
import { processClientSideDefaultRequestFields } from './shared-controller-modules/utils';

import { CONTROLLER_EVENTS } from 'controllers/events/controller-events';
import { convertHashParams, shouldHashOverrideDefaultValues, shouldSearchHashParams } from './converters/legacy-hash-params';

const { withoutKeys, withKeys } = merge;

export class SearchController implements Controller {
  public bundles = ['search'];
  public component = codeSplit(() => import(/* webpackChunkName: "search" */ 'views/hotel/search.tsx'));
  public pageId = `${SearchPageName.SA},H,20`;
  public path = '/Hotel-Search*';
  public exact = true;
  public routeName = 'search';
  public routeData = { rfrrNamespace: 'HOT.SR', pageName: 'page.Hotel-Search' };

  // eslint-disable-next-line complexity
  public async fetch(options: FetchOptions): Promise<any> {
    if (!options || !options.stores) {
      return Promise.reject('SearchController.fetch receieved an invalid StoreContainer');
    }

    const { isServer, query, stores, logger } = options;
    const fetchStartTime = Date.now();
    const context: ContextStore = stores.get<ContextStore>('context');
    const page: PageStore = stores.get<PageStore>('page');
    const analyticsStore: AnalyticsStore = stores.get<AnalyticsStore>('analytics');
    const searchCriteria: SearchCriteriaStore = stores.get<SearchCriteriaStore>('searchCriteria');
    const searchResults: SearchResultsStore = stores.get<SearchResultsStore>('searchResults');
    const multiItem: MultiItemStore = stores.get<MultiItemStore>('multiItem');
    const experiments: ExperimentStore = stores.get<ExperimentStore>('experiment');

    // sanitize incoming params
    const singleUseCriteria: any = withKeys(singleUseQueryParams).merge({}, options.query);
    const untrackedKeys = [...untrackedQueryParams, ...singleUseCriteria, 'prevQuery'];
    const prevQuery = withoutKeys(untrackedKeys).merge({}, query.prevQuery);
    const nextQuery = withoutKeys(untrackedKeys).merge({}, query);
    const packageType = getPackageType(query);

    this.initializeMultiItem(multiItem, query, nextQuery, context);

    searchResults.pageData = page;

    if (searchResults.foregoClientFetch) {
      searchResults.foregoClientFetch = false;
      const criteria = searchResults.searchCriteria.toJSON();
      const sqaParsedOut = SearchQueryAdapter.parseOut(criteria);

      return { query: sqaParsedOut, replace: true } as LocationReactorResult;
    }
    // if client side detects hash params, handle them
    if (!isServer && shouldSearchHashParams(nextQuery)) {
      const hashParams = convertHashParams();

      if (shouldHashOverrideDefaultValues(hashParams)) {
        searchResults.defaultRequestFields = {};
      }
      if (hashParams) {
        return { query: hashParams, replace: true } as LocationReactorResult;
      }
    }
    // server rendered with defaulted params, we need to adjust URLs first
    let newQuery = processClientSideDefaultRequestFields(isServer, searchResults, nextQuery);
    if (newQuery) {
      return newQuery;
    }

    const forceFetch = searchResults.shouldFetch;
    // check if queries are different to previous state
    if (forceFetch || !comparer.structural(nextQuery, prevQuery)) {
      // queries are meaningfully diff, setup base stores again
      searchCriteria.setValues({
        ...SearchCriteriaStore.getDefaults(),
        ...SearchCriteriaUtils.getFilterDefaults(),
        ...nextQuery,
      });

      const modelData = SearchQueryAdapter.parseIn(searchCriteria.toJSON());
      const incomingSearchCriteria = new SearchCriteriaModel(modelData);

      evaluateAndLogExperiment('LShop_federation_gateway_loom', context, experiments, page);

      // fetches are only carried out if criteria is also changed
      if (forceFetch || this.hasStoresChanged(searchResults.searchCriteria, incomingSearchCriteria)) {
        if (isServer === true && query && multiItem.getPackageType() === PackageType.FH) {
          const bucket = evaluateAndLogExperiment('MIS_Lodging_SRP_Enable_Client_Side_Rendering_', context, experiments, page);
          query.skipServerFetch = bucket === 1 ? 'true' : query.skipServerFetch;
        }
        const skipSearchResultsFetch = isServer === true && query && query.skipServerFetch === 'true';

        await this.doNetworking(
          page,
          analyticsStore,
          context,
          searchResults,
          incomingSearchCriteria,
          skipSearchResultsFetch,
          singleUseCriteria,
          searchCriteria,
          multiItem,
          packageType,
          experiments
        )
          .then(() => {
            if (logger) {
              logger.logEvent(
                CONTROLLER_EVENTS.SEARCH_CONTROLLER_FETCH,
                `fetchTime=${(Date.now() - fetchStartTime).toString()} ms`,
                `, isServer=${isServer}, ${getTrackingInfoForLogging(context)}`
              );
            }
          })
          .catch(error => {
            if (logger) {
              logger.logEvent(CONTROLLER_EVENTS.SEARCH_CONTROLLER_FETCH_FAILURE, error);
            }
          });
      }
    }
    // cleanup defaultRequestFields so they don't hang around for subsequent calls on clientside
    newQuery = processClientSideDefaultRequestFields(isServer, searchResults, nextQuery);
    if (newQuery) {
      return newQuery;
    }

    const destinationTitle = searchCriteria.destination || searchResults.searchedDestination;
    // `formatText` is deprecated. We should have the api create the localized page title and send it to the client.
    const pageTitle = destinationTitle
      ? formatText('hotels.searchResults.successfulSearch.pageTitle', destinationTitle)
      : formatText('hotels.searchResults.invalidSearch.pageTitle');
    page.title = pageTitle;
    // `document` is undefined on the server only set `document.title` on the client.
    if (!isServer) {
      document.title = pageTitle;
    }
  }

  private initializeMultiItem(multiItem: MultiItemStore, query: SerializedData, nextQuery: SerializedData, context: ContextStore) {
    multiItem.buildContext(query);
    multiItem.referrer = query.rfrr;

    if (query.multiProp === 'true' && !query.businessModelFilter && nextQuery) {
      const defaultBusinessModel = multiItem.getDefaultBusinessModel();
      if (multiItem.cartInfo && multiItem.cartInfo.identifier === 'mixAndMatch') {
        multiItem.removeMixAndMatchCart();
        multiItem.getOrCreateCart(context, defaultBusinessModel);
      }
      nextQuery.businessModelFilter = defaultBusinessModel;
    }
  }

  private async doNetworking(
    page: PageStore,
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    searchResults: SearchResultsStore,
    incomingSearchCriteria: SearchCriteriaModel,
    skipServerFetch: boolean,
    singleUseCriteria: SerializedData,
    searchCriteria: SearchCriteriaStore,
    multiItem: MultiItemStore,
    packageType: PackageType,
    experiments?: ExperimentStore
  ) {
    const pageFetch = page.fetch(meta['hotel-search'], context.site, context.locale);

    if (
      multiItem &&
      multiItem.isPackageShoppingPath() &&
      !multiItem.isServerSide() &&
      shouldCallMultiItemSessionMutation(searchResults.searchCriteria, incomingSearchCriteria, multiItem.getPackageType())
    ) {
      await searchResults.createSessionBeforeMultiItemFetch(multiItem, context, incomingSearchCriteria);

      // Setting the regionId to null so that correct regionId is retrieved in spinner for multicity searches.
      if (searchResults.searchCriteria.regionId !== incomingSearchCriteria.regionId) {
        incomingSearchCriteria.regionId = null;
      }
    }

    // Add an exception to allow for the filters to be updated if overfiltered
    if (!searchResults.isError || searchResults.overFiltered) {
      searchResults.updateSearchCriteria(incomingSearchCriteria);
      const searchResultsFetch = skipServerFetch
        ? Promise.resolve()
        : searchResults.fetch(analyticsStore, context, getSearchTimeout(packageType), singleUseCriteria, multiItem, page, experiments);

      await pageFetch;
      await searchResultsFetch;

      this.updateSearchCriteriaFromSearchResults(searchResults, searchCriteria);
    }
  }

  public fetchPageData(options: FetchPageOptions): Promise<PageData> {
    // Temporary fix for issue: https://jira.expedia.biz/browse/BHS-17025
    const noIndexSeo = { seo: { robots: 'noindex,nofollow' } };
    const packageType = getPackageType(options.query);
    const pageData = packageType ? this.getPackagePageData(packageType) : { navigationId: 'hotel' };

    return Promise.resolve({
      ...noIndexSeo,
      ...pageData,
    }) as Promise<PageData>;
  }

  private getPackagePageData(packageType: PackageType) {
    const pageName = SearchPageName[packageType];

    return {
      pageName,
      pageId: `${pageName},P,20`,
      navigationId: 'package',
    };
  }

  private updateSearchCriteriaFromSearchResults(searchResults, searchCriteria) {
    // BFF may provide regionId when coming from SEM or alternate dates
    if (searchResults.searchCriteria) {
      searchCriteria.startDate = searchResults.searchCriteria.startDate;
      searchCriteria.endDate = searchResults.searchCriteria.endDate;
      searchCriteria.regionId = searchResults.searchCriteria.regionId;
    }
  }

  private getComparableKeys(prevStore: SearchCriteriaModel, newStore: any) {
    let compareKeys = getSearchImpactingKeys();
    if (!this.didGeoChange(prevStore, newStore)) {
      compareKeys = compareKeys.filter(key => key !== 'regionId' && key !== 'latLong' && key !== 'destination');
    }

    // If we're asking to sort by values that's equivalent to what we currently have,
    // remove sort field from the comparison to avoid another service call
    const askingForEmptySortWhileWeHaveRecommended = newStore.sort === '' && prevStore.sort === PropertySort.RECOMMENDED;
    const askingForMctc2SortWhileWeHaveDeals = !newStore.sort && newStore.mctc === '2' && prevStore.sort === 'deals';
    const equivalentSort = askingForEmptySortWhileWeHaveRecommended || askingForMctc2SortWhileWeHaveDeals;
    if (equivalentSort) {
      compareKeys = compareKeys.filter(key => key !== 'sort');
    }

    return compareKeys;
  }

  private didGeoChange(prevStore: SearchCriteriaModel, newStore: any) {
    const { regionId: prevRegionId, latLong: prevLatLong, destination: prevDestination } = prevStore;
    const { regionId: newRegionId, latLong: newLatLong, destination: newDestination } = newStore;

    const isNewRegionIdEmpty = newRegionId === '';
    const isNewLatLongEmpty = newLatLong === '';
    const isNewDestinationEmpty = newDestination === '';
    const didRegionIdChange = !isNewRegionIdEmpty && newRegionId !== prevRegionId;
    const didLatLongChange = !isNewLatLongEmpty && newLatLong !== prevLatLong;
    const didDestinationChange = !isNewDestinationEmpty && newDestination !== prevDestination;

    return didRegionIdChange || (isNewRegionIdEmpty && (didLatLongChange || (isNewLatLongEmpty && didDestinationChange)));
  }

  private hasStoresChanged(prevStore: SearchCriteriaModel, newStore: SearchCriteriaModel) {
    if (Boolean(newStore) && !prevStore) {
      return true;
    }

    return SearchCriteriaUtils.hasSearchCriteriaChanged(prevStore, newStore, this.getComparableKeys(prevStore, newStore));
  }
}
